import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, CheckCircle2 } from '@digital-twins/design-system/icons'
import { authApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'
import { BackToLogin } from '@/components/auth/BackToLogin'

export default function ForgotPasswordPage() {
  const { t } = useTranslation(['auth', 'common'])
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      await authApi.forgotPassword(email)
      setSubmitted(true)
    } catch (err: any) {
      setError(err.response?.data?.message || t('forgot.errors.sendFailed'))
    } finally {
      setLoading(false)
    }
  }

  if (submitted) {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex flex-col items-center gap-4 text-center">
          <CheckCircle2 className="size-12 text-primary" />
          <Heading level={2}>{t('forgot.checkEmail')}</Heading>
          <Text as="p" color="muted" className="text-base">
            {t('forgot.sentBody', { email })}
          </Text>
        </div>
        <BackToLogin />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <Heading level={1} className="text-center">
          {t('forgot.title')}
        </Heading>
        <Text as="p" size="sm" color="muted" className="text-center">
          {t('forgot.subtitle')}
        </Text>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className="space-y-2">
          <Label htmlFor="email">{t('email', { ns: 'common' })}</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('emailPlaceholder')}
            required
            autoComplete="email"
          />
        </div>

        {error && (
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        <Button type="submit" variant="cta" disabled={loading} className="w-full">
          {loading ? (
            <>
              <Loader2 className="mr-2 size-4 animate-spin" />
              {t('forgot.submitting')}
            </>
          ) : (
            t('forgot.submit')
          )}
        </Button>
      </form>

      <BackToLogin />
    </div>
  )
}
